<script setup lang="ts">
import { useControlId } from '../../composables/useControlId'
import { computed, inject, onBeforeUnmount, ref, useAttrs, watch } from 'vue'
import { Eye, ImageOff } from '@lucide/vue'
import ImagePreview from './ImagePreview.vue'
import RenderContent from '../internal/RenderContent'
import RrSpin from '../spin/RrSpin.vue'
import { useUiConfig } from '../config-provider/context'
import { imageGroupKey, type ImageProps } from './types'

const props = withDefaults(defineProps<ImageProps>(), { src: '', alt: '', preview: true, previewMask: true, placeholder: false })
const emit = defineEmits<{ error: [event: Event]; load: [event: Event] }>()
defineOptions({ inheritAttrs: false })
const attrs = useAttrs()
const config = useUiConfig()
const group = inject(imageGroupKey, null)
const id = useControlId()
const failed = ref(false)
const usingFallback = ref(false)
const loaded = ref(false)
const innerOpen = ref(false)
const options = computed(() => typeof props.preview === 'object' ? props.preview : {})
const source = computed(() => usingFallback.value ? props.fallback! : props.src)
const previewEnabled = computed(() => props.preview !== false && !failed.value && Boolean(source.value) && (group?.enabled.value ?? true))
const previewSource = computed(() => options.value.src ?? source.value)
const open = computed(() => options.value.visible ?? innerOpen.value)
const dimensions = computed(() => ({ width: typeof props.width === 'number' ? `${props.width}px` : props.width, height: typeof props.height === 'number' ? `${props.height}px` : props.height }))
let unregister: (() => void) | undefined
watch([previewEnabled, previewSource], () => {
  if (!previewEnabled.value) { unregister?.(); unregister = undefined; return }
  if (group && !unregister) unregister = group.register(id, () => ({ src: previewSource.value, alt: props.alt }))
}, { immediate: true })
watch([() => props.src, () => props.fallback], () => { failed.value = false; usingFallback.value = false; loaded.value = false })
function setOpen(value: boolean) { const previous = open.value; innerOpen.value = value; if (value !== previous) options.value.onVisibleChange?.(value, previous) }
function showPreview() { if (!previewEnabled.value) return; if (group) group.open(id); else setOpen(true) }
function error(event: Event) {
  emit('error', event)
  if (props.fallback && !usingFallback.value) usingFallback.value = true
  else failed.value = true
}
function imageAttrs() { const { class: _class, style: _style, ...imageAttrs } = attrs; return imageAttrs }
onBeforeUnmount(() => unregister?.())
</script>

<template>
  <span class="rr-image" :class="[attrs.class, { 'is-previewable': previewEnabled }]" :style="[attrs.style as any, dimensions]" :role="previewEnabled ? 'button' : undefined" :tabindex="previewEnabled ? 0 : undefined" :aria-label="previewEnabled ? `${config.locale?.Image?.preview ?? '预览'} ${alt}` : undefined" @click="showPreview" @keydown.enter.prevent="showPreview" @keydown.space.prevent="showPreview">
    <span v-if="failed" class="rr-image__error" role="img" :aria-label="alt || '图片加载失败'"><ImageOff :size="24" aria-hidden="true" /><span>图片加载失败</span></span>
    <img v-else v-bind="imageAttrs()" class="rr-image__img" :src="source" :alt="alt" :width="width" :height="height" @error="error" @load="loaded = true; emit('load', $event)" />
    <span v-if="!loaded && !failed && (placeholder || $slots.placeholder)" class="rr-image__placeholder"><slot name="placeholder"><RrSpin size="small" /></slot></span>
    <span v-if="previewEnabled && previewMask !== false" class="rr-image__mask" :class="options.maskClassName"><slot name="previewMask"><RenderContent v-if="previewMask !== true" :value="previewMask" /><template v-else><Eye :size="16" aria-hidden="true" />{{ config.locale?.Image?.preview ?? '预览' }}</template></slot></span>
  </span>
  <ImagePreview v-if="!group" :open="previewEnabled && open" :images="[{ src: previewSource, alt }]" :current="0" :get-container="options.getContainer" @update:open="setOpen" />
</template>

<style scoped>
.rr-image { position: relative; display: inline-block; vertical-align: middle; max-width: 100%; overflow: hidden; border-radius: var(--rr-radius-control); }
.rr-image.is-previewable { cursor: pointer; }
.rr-image__img { display: block; max-width: 100%; width: 100%; height: 100%; object-fit: contain; }
.rr-image__mask { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; gap: var(--rr-space-1); opacity: 0; background: var(--rr-color-overlay-mask); color: var(--rr-color-surface); transition: opacity 120ms ease; }
.rr-image:hover .rr-image__mask, .rr-image:focus-visible .rr-image__mask { opacity: 1; }
.rr-image__placeholder, .rr-image__error { display: flex; align-items: center; justify-content: center; gap: var(--rr-space-2); background: var(--rr-color-surface-soft); color: var(--rr-color-text-muted); min-height: 48px; }
.rr-image__placeholder { position: absolute; inset: 0; }
.rr-image__error { padding: var(--rr-space-3); box-sizing: border-box; height: 100%; }
@media (prefers-reduced-motion: reduce) { .rr-image__mask { transition: none; } }
</style>
